import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchCrmEquipment, saveCrmEquipment, fetchDiscounts, replacementQuote, money2 } from "@/lib/dashboardApi";
import { Wrench, Calculator, Loader2, Lock, Plus, Trash2, Save } from "lucide-react";

const SYSTEM_TYPES = ["Central AC", "Heat Pump", "Mini-Split", "Air Handler", "Evaporator Coil", "Coil / Heat Kit", "Heat Kit", "Multi-Tenant", "Package Unit", "Ventilation", "Accessory"];
const MANUFACTURERS = ["ACiQ", "Rheem"];
const TABS = [{ key: "catalog", label: "Equipment Catalog" }, { key: "pricing", label: "Replacement Pricing" }];

export default function EquipmentHub() {
  const [tab, setTab] = useState("catalog");
  return (
    <div className="max-w-6xl" data-testid="equipment-hub">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold text-navy flex items-center gap-2"><Wrench className="h-6 w-6 text-launch" /> Equipment</h1>
        <p className="text-sm text-slate-500 mt-1">One unified system — ACiQ, Rheem, Mini-Splits, matched systems &amp; the replacement pricing builder.</p>
      </div>
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`equip-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 ${tab === t.key ? "border-launch text-navy" : "border-transparent text-slate-500 hover:text-navy"}`}>{t.label}</button>
        ))}
      </div>
      {tab === "catalog" ? <Catalog /> : <Pricing />}
    </div>
  );
}

function Catalog() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ system_type: "", manufacturer: "", search: "" });
  const load = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    setData(await fetchCrmEquipment(params));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;
  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-2 mb-2">
        <select value={f.manufacturer} onChange={(e) => setF({ ...f, manufacturer: e.target.value })} className="border border-slate-300 rounded px-2 py-2 text-sm" data-testid="equip-filter-mfr">
          <option value="">All manufacturers</option>{MANUFACTURERS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={f.system_type} onChange={(e) => setF({ ...f, system_type: e.target.value })} className="border border-slate-300 rounded px-2 py-2 text-sm" data-testid="equip-filter-system">
          <option value="">All system types</option>{SYSTEM_TYPES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <input placeholder="Search model #" value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} className="border border-slate-300 rounded px-2 py-2 text-sm" data-testid="equip-filter-search" />
      </div>
      <p className="text-xs text-slate-500 mb-3" data-testid="equip-count">{data.items.length} model{data.items.length === 1 ? "" : "s"}{(f.search || f.manufacturer || f.system_type) ? " (filtered)" : " in catalog"}</p>
      {!data.internal && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mb-3 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Costs are hidden for your role.</p>}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="equip-catalog-list">
        <div className="grid grid-cols-[1.4fr_.8fr_.7fr_.8fr] px-4 py-2.5 bg-slate-50 border-b text-[11px] font-bold uppercase text-slate-500">
          <span>Model</span><span>Capacity</span><span>Type</span><span>Cost</span>
        </div>
        {data.items.length === 0 && <p className="px-4 py-6 text-sm text-slate-500">No matching equipment.</p>}
        {data.items.map((it) => <Row key={it.id} it={it} canEdit={data.can_edit} internal={data.internal} reload={load} />)}
      </div>
    </div>
  );
}

function Row({ it, canEdit, internal, reload }) {
  const [cost, setCost] = useState(it.cost ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await saveCrmEquipment(it.id, { cost: cost === "" ? null : Number(cost) }); toast.success("Cost saved"); reload(); }
    catch (e) { toast.error("Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-[1.4fr_.8fr_.7fr_.8fr] items-center px-4 py-2.5 border-b border-slate-100 text-sm" data-testid={`equip-row-${it.id}`}>
      <span className="font-medium text-navy">{it.model_number}<span className="block text-[11px] text-slate-400 font-normal">{it.manufacturer} · {it.product_family || it.role}</span></span>
      <span className="text-slate-600">{it.capacity || "—"}</span>
      <span className="text-slate-600 capitalize">{it.role || it.system_type}</span>
      <span>
        {!internal ? <span className="text-slate-400">—</span>
          : canEdit ? (
            <span className="inline-flex items-center gap-1">
              <span className="text-slate-400">$</span>
              <input type="number" value={cost} placeholder="not set" onChange={(e) => setCost(e.target.value)} className="w-24 border border-slate-300 rounded px-2 py-1 text-sm" data-testid={`equip-cost-${it.id}`} />
              <button onClick={save} disabled={busy} className="text-emerald-600 p-1" data-testid={`equip-save-${it.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}</button>
            </span>
          ) : (it.cost == null ? <span className="text-slate-400 italic">not set</span> : money2(it.cost))}
      </span>
    </div>
  );
}

const TIERS = ["GOOD", "BETTER", "BEST"];

function Pricing() {
  const [tierItems, setTierItems] = useState({ GOOD: [{ label: "", cost: "" }], BETTER: [{ label: "", cost: "" }], BEST: [{ label: "", cost: "" }] });
  const [discounts, setDiscounts] = useState([]);
  const [picked, setPicked] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchDiscounts().then((d) => setDiscounts((d.items || []).filter((x) => x.amount != null && x.active))); }, []);

  const setItem = (tier, i, k, v) => setTierItems((s) => ({ ...s, [tier]: s[tier].map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = (tier) => setTierItems((s) => ({ ...s, [tier]: [...s[tier], { label: "", cost: "" }] }));
  const rmItem = (tier, i) => setTierItems((s) => ({ ...s, [tier]: s[tier].filter((_, idx) => idx !== i) }));

  const calc = async () => {
    setBusy(true);
    try {
      const tiers = TIERS.map((t) => ({ tier: t, items: tierItems[t].filter((i) => i.label || i.cost !== "").map((i) => ({ label: i.label, cost: i.cost === "" ? null : Number(i.cost) })) }))
        .filter((t) => t.items.length);
      if (!tiers.length) { toast.error("Add equipment/options to at least one tier"); setBusy(false); return; }
      setResult(await replacementQuote({ tiers, discount_ids: picked }));
    } catch (e) { toast.error("Quote failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="equip-pricing">
      <div className="grid md:grid-cols-3 gap-4">
        {TIERS.map((t) => (
          <div key={t} className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="font-heading font-bold text-navy mb-2">{t}</p>
            {tierItems[t].map((it, i) => (
              <div key={i} className="flex items-center gap-1 mb-2">
                <input placeholder="Equipment / option" value={it.label} onChange={(e) => setItem(t, i, "label", e.target.value)} className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" data-testid={`tier-${t}-label-${i}`} />
                <span className="text-slate-400 text-sm">$</span>
                <input type="number" placeholder="cost" value={it.cost} onChange={(e) => setItem(t, i, "cost", e.target.value)} className="w-20 border border-slate-300 rounded px-2 py-1 text-sm" data-testid={`tier-${t}-cost-${i}`} />
                <button onClick={() => rmItem(t, i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button onClick={() => addItem(t)} className="text-xs font-semibold text-navy hover:text-launch inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add item</button>
          </div>
        ))}
      </div>

      {discounts.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm font-semibold text-slate-600 mb-2">Discounts / Rebates (applied after base price)</p>
          <div className="flex flex-wrap gap-3">
            {discounts.map((d) => (
              <label key={d.id} className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={picked.includes(d.id)} onChange={() => setPicked((p) => p.includes(d.id) ? p.filter((x) => x !== d.id) : [...p, d.id])} data-testid={`disc-${d.id}`} />
                {d.name} ({d.kind === "percent" ? `${d.amount}%` : money2(d.amount)})
              </label>
            ))}
          </div>
        </div>
      )}

      <button onClick={calc} disabled={busy} data-testid="equip-calc-btn" className="inline-flex items-center gap-2 bg-navy hover:bg-navy/90 text-white font-bold px-5 py-2.5 rounded-lg disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Calculate pricing
      </button>

      {result && (
        <div className="grid md:grid-cols-3 gap-4" data-testid="equip-quote-result">
          {result.tiers.map((t) => (
            <div key={t.tier} className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="font-heading font-bold text-navy">{t.tier}</p>
              <p className="text-2xl font-extrabold text-navy mt-2" data-testid={`quote-final-${t.tier}`}>{money2(t.final_investment)}</p>
              <p className="text-[11px] text-slate-400 uppercase">Final Investment</p>
              {t.cost_incomplete && <p className="text-[11px] text-amber-600 mt-1">Some item costs not set</p>}
              {result.internal ? (
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 space-y-0.5" data-testid={`quote-internal-${t.tier}`}>
                  <div className="flex justify-between"><span>Equipment cost (C)</span><span>{money2(t.equipment_cost_C)}</span></div>
                  <div className="flex justify-between"><span>{t.formula}</span><span>{money2(t.base_selling_price)}</span></div>
                  {t.discount_total > 0 && <div className="flex justify-between text-emerald-600"><span>Discounts</span><span>−{money2(t.discount_total)}</span></div>}
                  <div className="flex justify-between"><span>Warranty (internal)</span><span>{money2(t.warranty_internal)}</span></div>
                  <div className="flex justify-between font-semibold text-navy"><span>Gross profit</span><span>{money2(t.gross_profit)} ({t.gross_margin_pct}%)</span></div>
                </div>
              ) : (
                <p className="mt-2 text-[11px] text-slate-400">Customer-facing — internal figures hidden.{t.discounts_applied ? ` Discounts −${money2(t.discounts_applied)}.` : ""}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
